import { useState, useCallback, useEffect } from 'react';
import { fetchData, fetchCurrentSessionState } from '../components/api/restapi';
import realTimeDeviceService from '../services/realTimeDeviceService';
import { formatLocalDateTime } from '../components/utils/formatDateTime';

function toIso(d) {
  return d instanceof Date ? d.toISOString() : new Date(d || Date.now()).toISOString();
}

function normalizeRdSessions(apiList, systemId) {
  const rows = [];
  const list = Array.isArray(apiList) ? apiList : [];
  for (const s of list) {
    if (systemId && (s.systemId || '') !== systemId) continue;
    rows.push({
      kind: 'rd',
      sessionId: s.sessionId || '',
      systemId: s.systemId || systemId || '',
      strategyLabel: s.collectorId || s.collector || 'RD Session',
      startedLabel: formatLocalDateTime(s.createdOn || s.startTime || null),
      status: 'Active',
    });
  }
  return rows;
}

function normalizeRtSessions(transformedList) {
  return transformedList
    .filter((s) => s && (s.status === 'running' || s.status === 'ACTIVE'))
    .map((s) => ({
      kind: 'rt',
      sessionId: s.sessionId || '',
      systemId: s.systemId || '',
      strategyLabel: s.configSummary || 'RT Session',
      startedLabel: formatLocalDateTime(s.startTime || null),
      status: 'Active',
    }));
}

/**
 * Per-device active-session hook.
 *
 * Returns:
 *   rdSessions   — normalized active RD session rows for this device
 *   rtSessions   — normalized active RT session rows for this device
 *   hasActiveRd  — authoritative gate (from /current-session-state, user-independent)
 *   hasActiveRt  — authoritative gate
 *   loading      — true while any fetch is in flight
 *   error        — string if gate call failed, null otherwise
 *   refresh      — re-run all fetches; call after a start/stop
 */
export default function useDeviceActiveSessions(systemId, userInfo, role) {
  const [state, setState] = useState({
    rdSessions: [],
    rtSessions: [],
    hasActiveRd: false,
    hasActiveRt: false,
    loading: false,
    error: null,
  });

  const refresh = useCallback(async () => {
    if (!systemId || !systemId.trim()) {
      setState({ rdSessions: [], rtSessions: [], hasActiveRd: false, hasActiveRt: false, loading: false, error: null });
      return;
    }

    setState((prev) => ({ ...prev, loading: true, error: null }));

    // ── Gate: authoritative per-device status, not user-filtered ──
    let hasActiveRd = false;
    let hasActiveRt = false;
    let gateError = null;
    try {
      const gateData = await fetchCurrentSessionState({ systemId: systemId.trim(), timestamp: toIso(new Date()) });
      hasActiveRd = gateData?.rdSession === 'ACTIVE';
      hasActiveRt = gateData?.rtSession === 'ACTIVE';
    } catch (err) {
      gateError = err?.message || 'Failed to load session state';
    }

    // ── Row detail: session lists for label/id/time ──
    let rdSessions = [];
    let rtSessions = [];
    try {
      const rdRes = await fetchData(
        `/api/v1/diagnostic-sessions?user=${encodeURIComponent(userInfo || '')}&sessionState=ACTIVE&userRole=${encodeURIComponent(role || 'Admin')}&page=1&size=100`
      );
      rdSessions = normalizeRdSessions(
        rdRes?.statusCode === 200 ? rdRes.activeRemoteDebugSessions : [],
        systemId.trim()
      );
    } catch (_) {}

    try {
      const rtList = await realTimeDeviceService.getActiveSessions(systemId.trim());
      rtSessions = normalizeRtSessions(rtList || []);
    } catch (_) {}

    // ── Reconciliation: if gate is ACTIVE but no row found, synthesize a placeholder ──
    if (hasActiveRd && rdSessions.length === 0) {
      rdSessions = [{ kind: 'rd', sessionId: '', systemId: systemId.trim(), strategyLabel: 'RD Session', startedLabel: '', status: 'Active' }];
    }
    if (hasActiveRt && rtSessions.length === 0) {
      rtSessions = [{ kind: 'rt', sessionId: '', systemId: systemId.trim(), strategyLabel: 'RT Session', startedLabel: '', status: 'Active' }];
    }

    // ── Fallback: if gate failed, derive from lists ──
    if (gateError) {
      hasActiveRd = rdSessions.length > 0;
      hasActiveRt = rtSessions.length > 0;
    }

    setState({ rdSessions, rtSessions, hasActiveRd, hasActiveRt, loading: false, error: gateError });
  }, [systemId, userInfo, role]);

  // Auto-fetch on mount and when the target device changes.
  useEffect(() => {
    refresh();
  }, [refresh]); // refresh identity changes when systemId/userInfo/role change

  return { ...state, refresh };
}
